"use client";
import React, { useEffect, useState } from "react";
import SectionHeading from "../others/SectionHeading";
import YearlyPerformanceChart from "../charts/YearlyPerformanceChart";
import { RawPerformanceRow } from "@/type/schemeType";
import InformationButton from "@/components/ui/InformationButton";

type BarData = {
  year: string;
  value: number;
};

type PeriodType = "monthly" | "quarterly" | "yearly";

export default function YearlyPerformanceSection({ fundSlug, id }: { fundSlug: string; id: number }) {
  const [category, setCategory] = useState<PeriodType>("monthly");
  const [data, setData] = useState<BarData[]>([]);

  useEffect(() => {
    fetch(`/api/performanceReturns?slug=${fundSlug}&id=${id}`)
      .then((res) => res.json())
      .then((raw: RawPerformanceRow[]) => {
        if (!raw.length) return;
        setData(transformData(raw, category));
      });
  }, [category, fundSlug, id]);

  const transformData = (raw: RawPerformanceRow[], type: typeof category): BarData[] => {
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonthIndex = currentDate.getMonth();

    if (type === "monthly") {
      const months: BarData[] = [];
      let year = currentYear;
      let monthIndex = currentMonthIndex;
      for (let i = 0; i < 8; i++) {
        const yearData = raw.find((d) => d.return_year === year);
        if (yearData) {
          const monthKey = monthNames[monthIndex].toLowerCase() + "_return";
          months.unshift({
            year: `${monthNames[monthIndex]} ${year}`,
            value: Number(yearData[monthKey as keyof RawPerformanceRow]) || 0,
          });
        }
        monthIndex--;
        if (monthIndex < 0) {
          monthIndex = 11;
          year--;
        }
      }
      return months;
    }

    if (type === "yearly") {
      const years = raw
        .map((d) => ({
          year: d.return_year.toString(),
          value: Number(d.monthly_return_percentage) || 0,
        }))
        .sort((a, b) => Number(a.year) - Number(b.year))
        .slice(-8);
      return years;
    }

    if (type === "quarterly") {
      const quarterLabels = ["Q1", "Q2", "Q3", "Q4"];
      const quarters: BarData[] = [];
      const currentQuarter = Math.floor(currentMonthIndex / 3) + 1;

      let year = currentYear;
      let q = currentQuarter;
      for (let i = 0; i < 8; i++) {
        const yearData = raw.find((d) => d.return_year === year);
        if (yearData) {
          const quarterKey = `quarter_${q}_return` as keyof RawPerformanceRow;
          quarters.unshift({
            year: `${quarterLabels[q - 1]} ${year}`,
            value: Number(yearData[quarterKey]) || 0,
          });
        }
        q--;
        if (q < 1) {
          q = 4;
          year--;
        }
      }
      return quarters;
    }

    return [];
  };

  if (data.length <= 0) return;

  return (
    <section>
      <div className="flex items-center gap-3 mb-2 sm:mb-4">
        <SectionHeading heading="Fund Performance" />
        <InformationButton keywords="yearly-performance" />
      </div>

      <div className="w-full bg-white rounded-4xl border border-[#E9E9EB] px-3 py-4 sm:p-5">
        <YearlyPerformanceChart data={data} />
        <div className="flex items-center justify-between mt-3">
          <div className="inline-flex mx-auto rounded-[100px] bg-[#EBF3FF] text-sm sm:text-base border border-[#1A51AA24] p-1">
            <button
              onClick={() => setCategory("monthly")}
              className={`px-2.5 sm:px-4 py-1 rounded-[100px] transition ${category === "monthly" ? "bg-white text-primaryBlue" : "text-[#202020A6] hover:text-primaryBlue"}`}
            >
              Monthly
            </button>
            <button
              onClick={() => setCategory("yearly")}
              className={`px-2.5 sm:px-4 py-1 rounded-[100px] transition ${category === "yearly" ? "bg-white text-primaryBlue" : "text-[#202020A6] hover:text-primaryBlue"}`}
            >
              Yearly
            </button>
            <button
              onClick={() => setCategory("quarterly")}
              className={`px-2.5 sm:px-4 py-1 rounded-[100px] transition ${category === "quarterly" ? "bg-white text-primaryBlue" : "text-[#202020A6] hover:text-primaryBlue"}`}
            >
              Quarterly
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
